import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  X, Loader2, CheckCircle2, AlertCircle, Receipt, CreditCard, ShoppingBag, TrendingUp, Repeat,
} from 'lucide-react'
import { useDuesThisMonth } from '../../../hooks/usePendingFinanceEvents'
import { cn } from '../../../lib/utils'

// Full-screen sheet that lists every recurring monthly finance item
// (installments, fixed expenses, income, special obligations) with its
// status for the current calendar month. The user picks what to settle
// now and one bulk RPC handles the rest in a transaction.
//
// Status legend:
//   - unsettled: no event yet for this month — needs to be created + confirmed
//   - pending:   event exists (cron created it), awaiting user confirm
//   - confirmed: already paid this month — read-only
//
// Both unsettled and pending rows are pickable; confirmed rows are
// disabled with a check mark for affordance.

function fmt(n) {
  if (n == null || isNaN(n)) return '—'
  const v = Number(n)
  const sign = v >= 0 ? '+' : '−'
  return `${sign}${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

const KIND_META = {
  installment: { icon: CreditCard, color: 'text-red-600' },
  fixed_expense: { icon: Receipt, color: 'text-red-600' },
  special_obligation: { icon: Repeat, color: 'text-red-600' },
  income: { icon: TrendingUp, color: 'text-emerald-600' },
}

export default function DuesSheet({ open, onClose }) {
  const { t } = useTranslation()
  const { dues, loading, payNow, unsettledTotal, unsettledCount, refetch } = useDuesThisMonth()
  const [selected, setSelected] = useState(() => new Set())
  const [overrides, setOverrides] = useState({})
  const [paying, setPaying] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  // Reset selection when sheet (re)opens. Default = everything that's
  // actionable (unsettled + pending) checked.
  useEffect(() => {
    if (!open) return
    setResult(null)
    setError(null)
    refetch()
  }, [open, refetch])

  useEffect(() => {
    if (!open || loading) return
    const initial = new Set()
    for (const d of dues) {
      if (d.status === 'unsettled' || d.status === 'pending') {
        initial.add(d.source_id)
      }
    }
    setSelected(initial)
    setOverrides({})
  }, [open, loading, dues])

  const togglePick = (sourceId) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(sourceId)) next.delete(sourceId)
      else next.add(sourceId)
      return next
    })
  }

  const total = useMemo(
    () => dues
      .filter((d) => selected.has(d.source_id) && d.status !== 'confirmed')
      .reduce((s, d) => {
        const override = overrides[d.source_id]
        return s + (override != null && !isNaN(override) ? Number(override) : Number(d.amount))
      }, 0),
    [dues, selected, overrides],
  )

  const handlePay = async () => {
    setPaying(true)
    setError(null)
    try {
      const sourceIds = Array.from(selected)
      // overrides keyed by event_id need a different shape (the RPC
      // works on event_ids, not source_ids) — but the payNow helper
      // creates events first then confirms, so we hand it the
      // source-keyed overrides and re-key inside if needed. For now
      // we only support overrides where event already exists.
      const eventOverrides = {}
      for (const d of dues) {
        if (selected.has(d.source_id) && d.event_id && overrides[d.source_id] != null) {
          eventOverrides[d.event_id] = overrides[d.source_id]
        }
      }
      const res = await payNow(sourceIds, eventOverrides)
      if (!res) throw new Error('confirm failed')
      setResult(res)
      setSelected(new Set())
      setOverrides({})
      // Tell any mounted finance surfaces to refetch — balance,
      // installments (remaining_months changed), and obligations
      // (next_payment_date advanced) all moved server-side.
      window.dispatchEvent(new CustomEvent('phoenix:refresh-finance'))
    } catch (e) {
      setError(e?.message || 'failed')
    } finally {
      setPaying(false)
    }
  }

  if (!open) return null

  const grouped = {
    income: dues.filter((d) => d.source_kind === 'income'),
    installment: dues.filter((d) => d.source_kind === 'installment'),
    fixed_expense: dues.filter((d) => d.source_kind === 'fixed_expense'),
    special_obligation: dues.filter((d) => d.source_kind === 'special_obligation'),
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/60 flex flex-col"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-background mt-auto rounded-t-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-input flex-shrink-0">
          <h2 className="flex-1 text-base font-semibold text-foreground" dir="auto">
            {t('dues.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-2 -me-1 rounded-md hover:bg-accent text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : dues.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" dir="auto">
              {t('dues.empty')}
            </p>
          ) : (
            <>
              {result && (
                <div className="rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 p-3 text-sm flex items-start gap-2" dir="auto">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('dues.success', { count: result.event_count })}</p>
                    {result.new_balance != null && (
                      <p className="text-xs opacity-80">
                        {t('dues.newBalance')}: <span className="font-mono">{Number(result.new_balance).toLocaleString('en-US')} SAR</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
              {error && (
                <div className="rounded-lg bg-red-500/10 text-red-700 dark:text-red-300 p-3 text-sm flex items-start gap-2" dir="auto">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {Object.entries(grouped).map(([kind, rows]) => {
                if (rows.length === 0) return null
                return (
                  <section key={kind}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2" dir="auto">
                      {t(`dues.kind.${kind}`)}
                    </h3>
                    <div className="space-y-1.5">
                      {rows.map((d) => (
                        <DueRow
                          key={d.source_id}
                          due={d}
                          checked={selected.has(d.source_id)}
                          override={overrides[d.source_id]}
                          onToggle={() => togglePick(d.source_id)}
                          onOverride={(v) => setOverrides((prev) => ({ ...prev, [d.source_id]: v }))}
                          t={t}
                        />
                      ))}
                    </div>
                  </section>
                )
              })}
            </>
          )}
        </div>

        {/* Footer / pay button */}
        {!loading && dues.length > 0 && (
          <div className="border-t border-input p-4 flex items-center gap-3 flex-shrink-0 bg-background">
            <div className="flex-1 min-w-0" dir="auto">
              <p className="text-[11px] text-muted-foreground">
                {t('dues.netTotal')}
              </p>
              <p className={cn(
                'text-base font-bold font-mono',
                total >= 0 ? 'text-emerald-600' : 'text-red-600',
              )} dir="ltr">
                {fmt(total)} SAR
              </p>
            </div>
            <button
              type="button"
              onClick={handlePay}
              disabled={paying || selected.size === 0}
              className={cn(
                'h-11 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors',
                paying || selected.size === 0
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-app-primary-500 text-white hover:bg-app-primary-500/90',
              )}
            >
              {paying && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('dues.confirmSelected', { count: selected.size })}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

function DueRow({ due, checked, override, onToggle, onOverride, t }) {
  const { icon: Icon, color } = KIND_META[due.source_kind] || { icon: ShoppingBag, color: 'text-muted-foreground' }
  const isConfirmed = due.status === 'confirmed'
  const isPending = due.status === 'pending'
  const [editing, setEditing] = useState(false)
  const displayedAmount = override != null && override !== '' ? override : due.amount

  return (
    <div className={cn(
      'flex items-center gap-3 px-3 py-2.5 rounded-lg border',
      isConfirmed ? 'opacity-60 border-emerald-500/20 bg-emerald-500/5' : 'border-input',
    )}>
      {isConfirmed ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
      ) : (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="w-4 h-4 flex-shrink-0 accent-app-primary-500"
          aria-label={due.label}
        />
      )}
      <Icon className={cn('w-4 h-4 flex-shrink-0', color)} />
      <div className="flex-1 min-w-0" dir="auto">
        <p className="text-sm font-medium text-foreground truncate">{due.label}</p>
        <p className="text-[11px] text-muted-foreground">
          {isConfirmed
            ? t('dues.statusConfirmed')
            : isPending
              ? t('dues.statusPending')
              : t('dues.statusUnsettled', { day: due.recurring_day })}
        </p>
      </div>
      {editing && !isConfirmed ? (
        <input
          type="number"
          value={displayedAmount}
          onChange={(e) => onOverride(e.target.value)}
          onBlur={() => setEditing(false)}
          autoFocus
          className="w-24 h-8 px-2 rounded border border-input bg-background text-sm font-mono text-end"
        />
      ) : (
        <button
          type="button"
          onClick={() => !isConfirmed && setEditing(true)}
          disabled={isConfirmed}
          className={cn(
            'text-sm font-mono font-semibold flex-shrink-0',
            Number(displayedAmount) >= 0 ? 'text-emerald-600' : 'text-red-600',
            !isConfirmed && 'hover:underline',
          )}
          dir="ltr"
        >
          {fmt(displayedAmount)}
        </button>
      )}
    </div>
  )
}
